import React, { useCallback, useMemo } from 'react';
import { calcMilestone, MILESTONES } from '@onward-delivery/core';
import { addDays, isSameDay } from 'date-fns';
import { defaultSettings } from './index';
import { EXCEPTION_TYPES } from '@onward-delivery/core';

const getTimeAmount = (units, value) => {
    const unitMap = {
        Hours: 3600000,
        Days: 86400000,
    };
    return unitMap[units] * value;
};

const rowColors = {
    red: ['#f7e2e2', '#efd5d5'],
    orange: ['#f5dfc6', '#f0cca1'],
    grey: ['#ffffff', '#f5f5f5'],
    blue: ['#e2e5f7', '#cad1fb'],
    purple: ['#efd8f2', '#ebc2f0'],
};
const hoverColors = {
    red: '#dba6a6',
    orange: '#dea750',
    grey: '#d1d1d1',
    blue: '#8c99e5',
    purple: '#ce89d6',
};

const getOrderColorKey = ({ order, clientIds }) => {
    let color = 'grey';

    const isCarrier = !!order.carrier_id && (clientIds || []).includes(order.carrier_id);

    const tmsUser = order?.order_carrier || order?.order_shipper;
    let settings = order.order_shipper?.milestone_threshold_settings;
    if (order.oms) {
        if (!tmsUser?.user?.circles?.['carrier-milestone-thresholds']) {
            return color;
        }
        settings = tmsUser?.milestone_threshold_settings;
    }

    const thresholds = settings || defaultSettings;
    const milestone = calcMilestone(order);

    // Check for any attempts and highlight blue.
    const events = order?.wh_events || [];
    if (
        events.some((e) => {
            return e.action.split(':')?.[1] === 'ATTEMPT';
        })
    ) {
        color = 'blue';
    }

    // If the order is on hold, highlight purple.
    if (order?.order_status === 'on_hold') {
        color = 'purple';
    }

    // If order has OSD exceptions, highlight blue.
    if (
        (order?.exceptions || []).some((e) => {
            return (
                [EXCEPTION_TYPES.OVERAGE, EXCEPTION_TYPES.SHORTAGE, EXCEPTION_TYPES.DAMAGED].includes(e.type) &&
                e.status !== 'RESOLVED'
            );
        })
    ) {
        color = 'blue';
    }

    if (color === 'grey') {
        switch (milestone) {
            case MILESTONES.ORDER_TENDERED: {
                const errThreshold = thresholds?.orderPlacedToScheduled?.error;
                const warningThreshold = thresholds?.orderPlacedToScheduled?.warning;
                const warningAmount = getTimeAmount(warningThreshold.units, warningThreshold.value);
                const errAmount = getTimeAmount(errThreshold.units, errThreshold.value);
                const createdTime = new Date(order?.created_at).getTime();
                const warningTimeAfterEvent = new Date(createdTime + warningAmount);
                const errTimeAfterEvent = new Date(createdTime + errAmount);
                const now = new Date();
                if (now > warningTimeAfterEvent) {
                    color = 'orange';
                }
                if (now > errTimeAfterEvent) {
                    color = 'red';
                }
                break;
            }

            case MILESTONES.MM_PENDING: {
                if (isCarrier) {
                    break;
                }

                const estShipDate = order?.wh_events?.find((e) => e.action === 'START:AWAITING')?.est_ship_date;
                if (estShipDate) {
                    break;
                }

                const errThreshold = thresholds?.orderPlacedToScheduled?.error;
                const warningThreshold = thresholds?.orderPlacedToScheduled?.warning;
                const warningAmount = getTimeAmount(warningThreshold.units, warningThreshold.value);
                const errAmount = getTimeAmount(errThreshold.units, errThreshold.value);

                const createdDate = new Date(order?.created_at).getTime();
                const warningTimeAfterEvent = new Date(createdDate + warningAmount);
                const errTimeAfterEvent = new Date(createdDate + errAmount);

                const now = new Date();

                if (now > warningTimeAfterEvent) {
                    color = 'orange';
                }
                if (now > errTimeAfterEvent) {
                    color = 'red';
                }
                break;
            }

            // In transit shares the scheduled clock: both run from est_ship_date to arrival.
            // Without a case it stays grey forever and drops out of the Distressed tab.
            case MILESTONES.MM_IN_TRANSIT:
            case MILESTONES.MM_SCHEDULED: {
                if (isCarrier) {
                    break;
                }

                const errThreshold = thresholds?.pickupScheduledToArrived?.error;
                const warningThreshold = thresholds?.pickupScheduledToArrived?.warning;
                const warningAmount = getTimeAmount(warningThreshold.units, warningThreshold.value);
                const errAmount = getTimeAmount(errThreshold.units, errThreshold.value);
                const estShipDate = order?.wh_events?.find((e) => !!e.est_ship_date)?.est_ship_date;
                if (!estShipDate) {
                    break;
                }
                const estShipDateTime = new Date(estShipDate).getTime();
                const warningTimeAfterEvent = new Date(estShipDateTime + warningAmount);
                const errTimeAfterEvent = new Date(estShipDateTime + errAmount);
                const now = new Date();
                if (now > warningTimeAfterEvent) {
                    color = 'orange';
                }
                if (now > errTimeAfterEvent) {
                    color = 'red';
                }
                break;
            }

            case MILESTONES.PICKUP_SCHEDULED: {
                if (isCarrier) {
                    break;
                }

                const timeframeEnd = order.pickup_window_end;

                // If there is no timeframe, check if it is 8 hours after the scheduled date
                if (!timeframeEnd) {
                    const scheduledDate = order.pickup_date;
                    if (!scheduledDate) {
                        break;
                    }
                    const timeAfterEvent = new Date(new Date(scheduledDate).getTime() + 8 * 3600000);
                    const now = new Date();
                    if (now > timeAfterEvent) {
                        color = 'red';
                    }
                    break;
                }

                // If there is a timeframe end, highlight red if it is after the timeframe end
                const dateTimeframeEnd = new Date(timeframeEnd);
                const now = new Date();
                if (now > dateTimeframeEnd) {
                    color = 'red';
                    break;
                }

                // Otherwise highlight orange if it is after the timeframe start only
                const timeframeStart = order.pickup_window_start;
                const dateTimeframeStart = new Date(timeframeStart);
                if (now > dateTimeframeStart) {
                    color = 'orange';
                }

                break;
            }

            case MILESTONES.MM_ARRIVED: {
                if (isCarrier) {
                    break;
                }

                // orders.arrived_date covers crossdock legs: the integration meta exists only for
                // integration-booked orders, and core maps MM_CD_ARRIVED for any warehouse-bound
                // one -- without the fallback those stay grey and never reach the Distressed tab.
                const arrivedDate = order?.middle_mile_integration_meta?.arrived_date ?? order?.arrived_date;
                if (!arrivedDate) {
                    break;
                }
                const arrivedDateTime = new Date(arrivedDate).getTime();

                const warningThreshold = thresholds.arrivedToReceived.warning;
                const errThreshold = thresholds.arrivedToReceived.error;
                const warningAmount = getTimeAmount(warningThreshold.units, warningThreshold.value);
                const errAmount = getTimeAmount(errThreshold.units, errThreshold.value);
                const warningTimeAfterEvent = new Date(arrivedDateTime + warningAmount);
                const errTimeAfterEvent = new Date(arrivedDateTime + errAmount);
                const now = new Date();
                if (now > warningTimeAfterEvent) {
                    color = 'orange';
                }
                if (now > errTimeAfterEvent) {
                    color = 'red';
                }
                break;
            }

            case MILESTONES.RECEIVED: {
                const warningThreshold = thresholds.receivedToScheduled.warning;
                const errThreshold = thresholds.receivedToScheduled.error;
                const warningAmount = getTimeAmount(warningThreshold.units, warningThreshold.value);
                const errAmount = getTimeAmount(errThreshold.units, errThreshold.value);
                const receivedDate = order.wh_events.find((e) => e.status === 'RECEIVED')?.received_date;
                if (!receivedDate) {
                    break;
                }
                const receivedDateTime = new Date(receivedDate).getTime();
                const warningTimeAfterEvent = new Date(receivedDateTime + warningAmount);
                const errTimeAfterEvent = new Date(receivedDateTime + errAmount);
                const now = new Date();
                if (now > warningTimeAfterEvent) {
                    color = 'orange';
                }
                if (now > errTimeAfterEvent) {
                    color = 'red';
                }
                break;
            }

            // Not customizable, instead highlights after 8 hours
            case MILESTONES.DELIVERY_SCHEDULED: {
                const scheduledDate = order?.order_type === 'return' ? order.pickup_date : order.delivery_date;
                if (!scheduledDate) {
                    break;
                }
                const timeAfterEvent = new Date(new Date(scheduledDate).getTime() + 8 * 3600000);
                const now = new Date();
                if (now > timeAfterEvent) {
                    color = 'red';
                }
                break;
            }

            case MILESTONES.DELIVERY_EN_ROUTE: {
                const timeframeEnd = order.del_window_end;

                // If there is no timeframe, check if it is 8 hours after the scheduled date
                if (!timeframeEnd) {
                    const scheduledDate = order.delivery_date;
                    if (!scheduledDate) {
                        break;
                    }
                    const timeAfterEvent = new Date(new Date(scheduledDate).getTime() + 8 * 3600000);
                    const now = new Date();
                    if (now > timeAfterEvent) {
                        color = 'red';
                    }
                    break;
                }

                // If there is a timeframe end, highlight red if it is after the timeframe end
                const dateTimeframeEnd = new Date(timeframeEnd);
                const now = new Date();
                if (now > dateTimeframeEnd) {
                    color = 'red';
                    break;
                }

                // Otherwise highlight orange if it is after the timeframe start only
                let timeframeStart;
                if (order.order_type === 'return') {
                    timeframeStart = order.pickup_window_start;
                } else {
                    timeframeStart = order.del_window_start;
                }
                const dateTimeframeStart = new Date(timeframeStart);
                if (now > dateTimeframeStart) {
                    color = 'orange';
                }

                break;
            }

            // An invoice replaces the Delivered chip, but missing proof still runs this clock.
            // Approved and Paid also label orders that already have a POD or signature, and
            // returns, so those stay grey.
            case MILESTONES.DELIVERY_COMPLETE:
            case MILESTONES.INVOICED_INCOMPLETE:
            case MILESTONES.INVOICE_APPROVED:
            case MILESTONES.INVOICE_PAID: {
                const isReturn = order.order_type === 'return' || order.order_type === 'wh_return';
                const hasProof = !!(order.pod?.length || order.customer_signature);
                if (
                    milestone !== MILESTONES.DELIVERY_COMPLETE &&
                    milestone !== MILESTONES.INVOICED_INCOMPLETE &&
                    (isReturn || hasProof)
                ) {
                    break;
                }

                const warningThreshold = thresholds.deliveredToComplete.warning;
                const errThreshold = thresholds.deliveredToComplete.error;
                const warningAmount = getTimeAmount(warningThreshold.units, warningThreshold.value);
                const errAmount = getTimeAmount(errThreshold.units, errThreshold.value);
                const deliveredDate = order.completion_time;
                if (!deliveredDate) {
                    break;
                }
                const deliveredDateTime = new Date(deliveredDate).getTime();
                const warningTimeAfterEvent = new Date(deliveredDateTime + warningAmount);
                const errTimeAfterEvent = new Date(deliveredDateTime + errAmount);
                const now = new Date();
                if (now > warningTimeAfterEvent) {
                    color = 'orange';
                }
                if (now > errTimeAfterEvent) {
                    color = 'red';
                }
                break;
            }

            // case MILESTONES.PICKUP_COMPLETE: {
            //     const threshold = thresholds.pickup.pickupCompleteToDelivered;
            //     const pickupCompletedDate = order.ltl_pickup_completed;
            //     if (pickupCompletedDate) {
            //         if (threshold.value === 'Day Of') {
            //             const today = new Date();
            //             if (isSameDay(pickupCompletedDate, today) || pickupCompletedDate > today) {
            //                 color = 'red';
            //             }
            //         } else if (threshold.value === 'Next Day') {
            //             const today = new Date();
            //             const tomorrow = addDays(today, 1);
            //             if (isSameDay(pickupCompletedDate, tomorrow) || pickupCompletedDate > tomorrow) {
            //                 color = 'red';
            //             }
            //         }
            //     }
            //     break;
            // }
        }
    }

    return color;
};

export const isOrderDistressed = ({ order, clientIds }) => {
    return getOrderColorKey({ order, clientIds }) === 'red';
};

export const useOrderColor = () => {
    return useCallback(({ order, index, clientIds }) => {
        const color = getOrderColorKey({ order, clientIds });

        return [rowColors[color][index % 2] || '', hoverColors[color] || ''];
    }, []);
};
